<template>
  <div class="relative w-full" ref="dropdownContainer">
    <!-- Dropdown (Custom Select Box) -->
    <div
      class="w-full p-2 border rounded cursor-pointer flex justify-between items-center border-stone-400"
      @click="toggleDropdown"
    >
      <!-- Show Selected Items as Chips Inside the Input -->
      <div class="flex flex-wrap gap-2 flex-1">
        <template v-if="selectedItems.length > 0">
          <div
            v-for="(item, index) in selectedItems"
            :key="item.id"
            class="flex items-center bg-blue-500 text-white px-3 py-1 rounded-full"
          >
            <span>{{ item.name }}</span>
            <button
              type="button"
              @click.stop="removeItem(item)"
              class="ml-2 text-white focus:outline-none"
            >
              &times;
            </button>
          </div>
        </template>
        <span v-else class="text-stone-500">{{ label }}</span>
      </div>

      <!-- Dropdown Arrow -->
      <svg
        :class="['w-4 h-4 transition-transform', { 'rotate-180': isDropdownOpen }]"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="2"
      >
        <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </div>

    <!-- Dropdown Options -->
    <div
      v-if="isDropdownOpen"
      class="absolute left-0 right-0 bg-white border rounded mt-1 shadow-lg max-h-40 overflow-y-auto z-10"
    >
      <div
        v-for="option in availableOptions"
        :key="option.id"
        @click="toggleItem(option)"
        class="p-2 hover:bg-gray-200 cursor-pointer"
      >
        {{ option.name }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, onMounted, onUnmounted } from "vue";

const props = defineProps({
  modelValue: {
    type: Array,
    required: true,
  },
  options: {
    type: Array,
    required: true,
  },
  label: {
    type: String,
    default: "Select",
  },
});

const emit = defineEmits(["update:modelValue"]);
const selectedItems = computed({
  get: () => props.modelValue,
  set: (newValue) => emit("update:modelValue", newValue),
});

const isDropdownOpen = ref(false);
const dropdownContainer = ref(null);

// Toggle dropdown
const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

// Close dropdown when clicking outside
const handleClickOutside = (event) => {
  if (dropdownContainer.value && !dropdownContainer.value.contains(event.target)) {
    isDropdownOpen.value = false;
  }
};

// Lifecycle Hooks
onMounted(() => document.addEventListener("click", handleClickOutside));
onUnmounted(() => document.removeEventListener("click", handleClickOutside));

// Filter available options to exclude already selected ones
const availableOptions = computed(() =>
  props.options.filter(
    (option) => !selectedItems.value.some((selected) => selected.id === option.id)
  )
);

// Function to add/remove an item
const toggleItem = (item) => {
  if (!item) return;
  if (selectedItems.value.some((selected) => selected.id === item.id)) {
    removeItem(item);
  } else {
    selectedItems.value = [...selectedItems.value, item];
  }
};

// Function to remove an item chip
const removeItem = (item) => {
  selectedItems.value = selectedItems.value.filter((i) => i.id !== item.id);
};
</script>
